import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ColumnConfig,
  TableConfigState,
  TableColumnState,
  SortConfig,
  FilterConfig,
} from '@/shared/types/table';

const CONFIG_VERSION = 1;
const STORAGE_PREFIX = 'table-config-';

interface UseTableConfigOptions {
  /** Clave única para identificar esta tabla en localStorage */
  configKey?: string;
  /** Habilitar persistencia en localStorage */
  persistConfig?: boolean;
  /** Persistir filtros y búsqueda en la misma configKey (por defecto true) */
  queryPersist?: boolean;
  /** Columnas iniciales de la tabla */
  initialColumns: ColumnConfig[];
  /** Ordenamiento inicial */
  initialSort?: SortConfig;
  /** Filtros iniciales */
  initialFilters?: FilterConfig[];
  /** Callback cuando cambia la configuración */
  onConfigChange?: (config: TableConfigState, changeType: string) => void;
}

interface UseTableConfigReturn {
  /** Columnas con la configuración aplicada */
  columns: ColumnConfig[];
  /** Ordenamiento actual */
  sort?: SortConfig;
  /** Filtros actuales */
  filters: FilterConfig[];
  /** Texto de búsqueda persistido */
  searchQuery: string;
  /** Actualizar orden de columnas */
  updateColumnOrder: (newColumns: ColumnConfig[]) => void;
  /** Actualizar visibilidad de columna */
  updateColumnVisibility: (columnId: string, visible: boolean) => void;
  /** Actualizar visibilidad de múltiples columnas de una vez */
  updateMultipleColumnVisibility: (updates: Array<{ columnId: string; visible: boolean }>) => void;
  /** Actualizar bloqueo de columna */
  updateColumnLock: (columnId: string, locked: boolean) => void;
  /** Actualizar ancho de columna. defaultWidthAtStart = ancho al inicio del resize (para poder volver al mínimo). */
  updateColumnWidth: (
    columnId: string,
    width: number | string,
    defaultWidthAtStart?: number
  ) => void;
  /** Actualizar ordenamiento */
  updateSort: (newSort: SortConfig) => void;
  /** Actualizar filtros */
  updateFilters: (newFilters: FilterConfig[]) => void;
  /** Actualizar texto de búsqueda */
  setSearchQuery: (query: string) => void;
  /** Resetear configuración a valores iniciales */
  resetConfig: () => void;
  /** Configuración actual completa */
  currentConfig: TableConfigState;
}

/**
 * Hook para manejar la persistencia de configuración de tabla en localStorage
 *
 * @example
 * ```tsx
 * const { columns, updateColumnOrder, updateColumnLock } = useTableConfig({
 *   configKey: 'my-table',
 *   persistConfig: true,
 *   initialColumns: myColumns,
 * })
 * ```
 */
export const useTableConfig = ({
  configKey,
  persistConfig = false,
  queryPersist = true,
  initialColumns,
  initialSort,
  initialFilters = [],
  onConfigChange,
}: UseTableConfigOptions): UseTableConfigReturn => {
  const storageKey = configKey ? `${STORAGE_PREFIX}${configKey}` : null;

  // Función para crear configuración inicial
  const createInitialConfig = useCallback((): TableConfigState => {
    return {
      version: CONFIG_VERSION,
      updatedAt: new Date().toISOString(),
      columns: initialColumns.map((col, index) => ({
        id: col.id,
        order: index,
        visible: col.visible !== false,
        locked: col.locked || false,
        width: col.width,
        defaultWidth: typeof col.width === 'number' ? col.width : undefined,
      })),
      sort: initialSort,
      filters: initialFilters,
      searchQuery: '',
    };
  }, [initialColumns, initialSort, initialFilters]);

  // Función para cargar configuración desde localStorage
  const loadConfig = useCallback((): TableConfigState | null => {
    if (!storageKey || !persistConfig) return null;

    try {
      const stored = localStorage.getItem(storageKey);
      if (!stored) return null;

      const parsed = JSON.parse(stored) as TableConfigState;

      // Verificar versión
      if (parsed.version !== CONFIG_VERSION) {
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.warn(
            `[useTableConfig] Config version mismatch, resetting config for ${configKey}`,
          );
        }
        return null;
      }

      // Si queryPersist es false, no usar filtros ni búsqueda guardados
      if (!queryPersist) {
        return {
          ...parsed,
          filters: initialFilters,
          searchQuery: '',
        };
      }

      return parsed;
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.error(`[useTableConfig] Error loading config for ${configKey}:`, error);
      }
      return null;
    }
  }, [storageKey, persistConfig, queryPersist, configKey, initialFilters]);

  // Función para guardar configuración en localStorage (solo persiste query si queryPersist es true)
  const saveConfig = useCallback(
    (config: TableConfigState) => {
      if (!storageKey || !persistConfig) return;

      const toSave: TableConfigState = queryPersist
        ? config
        : { ...config, filters: initialFilters, searchQuery: '' };

      try {
        localStorage.setItem(storageKey, JSON.stringify(toSave));
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.error(`[useTableConfig] Error saving config for ${configKey}:`, error);
        }
      }
    },
    [storageKey, persistConfig, queryPersist, configKey, initialFilters],
  );

  // Estado de configuración
  const [config, setConfig] = useState<TableConfigState>(() => {
    const loaded = loadConfig();
    return loaded || createInitialConfig();
  });

  // Sincronizar con localStorage cuando cambie initialColumns (si no hay config guardada)
  useEffect(() => {
    if (!loadConfig()) {
      setConfig(createInitialConfig());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- createInitialConfig/loadConfig estables, solo re-ejecutar cuando cambie initialColumns
  }, [initialColumns.length]);

  // Función para actualizar configuración
  const updateConfig = useCallback(
    (updates: Partial<TableConfigState>, changeType: string) => {
      setConfig((prev) => {
        const newConfig: TableConfigState = {
          ...prev,
          ...updates,
          updatedAt: new Date().toISOString(),
        };

        // Guardar en localStorage
        saveConfig(newConfig);

        // Notificar cambio
        onConfigChange?.(newConfig, changeType);

        return newConfig;
      });
    },
    [saveConfig, onConfigChange],
  );

  // Aplicar configuración a las columnas
  const columns = useCallback((): ColumnConfig[] => {
    const columnMap = new Map(initialColumns.map((col) => [col.id, col]));

    // Ordenar columnas según la configuración guardada
    const sortedColumns: ColumnConfig[] = [];
    const configColumnIds = new Set(config.columns.map((c) => c.id));

    // Primero agregar columnas según el orden guardado
    config.columns
      .sort((a, b) => a.order - b.order)
      .forEach((colState) => {
        const originalCol = columnMap.get(colState.id);
        if (originalCol) {
          const width = colState.width ?? originalCol.width;
          const originalWidth =
            colState.defaultWidth ??
            (typeof originalCol.width === 'number' ? originalCol.width : undefined) ??
            originalCol.originalWidth;
          sortedColumns.push({
            ...originalCol,
            visible: colState.visible,
            locked: colState.locked,
            width,
            ...(originalWidth !== undefined && { originalWidth }),
          });
        }
      });

    // Agregar columnas nuevas que no estaban en la configuración guardada
    initialColumns.forEach((col) => {
      if (!configColumnIds.has(col.id)) {
        sortedColumns.push(col);
      }
    });

    return sortedColumns;
  }, [initialColumns, config.columns]);

  // Actualizar orden de columnas
  const updateColumnOrder = useCallback(
    (newColumns: ColumnConfig[]) => {
      const newColumnStates: TableColumnState[] = newColumns.map((col, index) => {
        const existingState = config.columns.find((c) => c.id === col.id);
        return {
          id: col.id,
          order: index,
          visible: existingState?.visible ?? col.visible !== false,
          locked: col.locked || existingState?.locked || false,
          width: col.width ?? existingState?.width,
          defaultWidth: existingState?.defaultWidth,
        };
      });

      updateConfig({ columns: newColumnStates }, 'column-reorder');
    },
    [config.columns, updateConfig],
  );

  // Actualizar visibilidad de columna
  const updateColumnVisibility = useCallback(
    (columnId: string, visible: boolean) => {
      const newColumns = config.columns.map((col) =>
        col.id === columnId ? { ...col, visible } : col,
      );
      updateConfig({ columns: newColumns }, 'column-visibility');
    },
    [config.columns, updateConfig],
  );

  // Actualizar visibilidad de múltiples columnas de una vez (para evitar sobreescrituras)
  const updateMultipleColumnVisibility = useCallback(
    (updates: Array<{ columnId: string; visible: boolean }>) => {
      const updateMap = new Map(updates.map((u) => [u.columnId, u.visible]));
      const newColumns = config.columns.map((col) => {
        if (updateMap.has(col.id)) {
          return { ...col, visible: updateMap.get(col.id)! };
        }
        return col;
      });
      updateConfig({ columns: newColumns }, 'column-visibility');
    },
    [config.columns, updateConfig],
  );

  // Actualizar bloqueo de columna
  const updateColumnLock = useCallback(
    (columnId: string, locked: boolean) => {
      const newColumns = config.columns.map((col) =>
        col.id === columnId ? { ...col, locked } : col,
      );

      // Si se bloquea, mover al inicio
      if (locked) {
        const lockedColumns = newColumns.filter((c) => c.locked);
        const unlockedColumns = newColumns.filter((c) => !c.locked);
        const reorderedColumns = [...lockedColumns, ...unlockedColumns].map((col, index) => ({
          ...col,
          order: index,
        }));
        updateConfig({ columns: reorderedColumns }, 'column-lock');
      } else {
        updateConfig({ columns: newColumns }, 'column-lock');
      }
    },
    [config.columns, updateConfig],
  );

  // Actualizar ancho de columna. Si se pasa defaultWidthAtStart (ancho al inicio del resize), se guarda como mínimo para poder volver.
  const updateColumnWidth = useCallback(
    (columnId: string, width: number | string, defaultWidthAtStart?: number) => {
      const newColumns = config.columns.map((col) => {
        if (col.id !== columnId) return col;
        const defaultWidth =
          defaultWidthAtStart !== null && defaultWidthAtStart !== undefined
            ? defaultWidthAtStart
            : col.defaultWidth;
        return {
          ...col,
          width,
          ...(defaultWidth !== null && defaultWidth !== undefined && { defaultWidth }),
        };
      });
      updateConfig({ columns: newColumns }, 'column-resize');
    },
    [config.columns, updateConfig],
  );

  // Actualizar ordenamiento
  const updateSort = useCallback(
    (newSort: SortConfig) => {
      updateConfig({ sort: newSort }, 'sort');
    },
    [updateConfig],
  );

  // Actualizar filtros
  const updateFilters = useCallback(
    (newFilters: FilterConfig[]) => {
      updateConfig({ filters: newFilters }, 'filter');
    },
    [updateConfig],
  );

  const setSearchQuery = useCallback(
    (query: string) => {
      updateConfig({ searchQuery: query }, 'search');
    },
    [updateConfig],
  );

  // Resetear configuración
  const resetConfig = useCallback(() => {
    const initial = createInitialConfig();
    setConfig(initial);

    if (storageKey && persistConfig) {
      localStorage.removeItem(storageKey);
    }

    onConfigChange?.(initial, 'reset');
  }, [createInitialConfig, storageKey, persistConfig, onConfigChange]);

  const computedColumns = useMemo(() => columns(), [columns]);

  return {
    columns: computedColumns,
    sort: config.sort,
    filters: config.filters || [],
    searchQuery: config.searchQuery ?? '',
    updateColumnOrder,
    updateColumnVisibility,
    updateMultipleColumnVisibility,
    updateColumnLock,
    updateColumnWidth,
    updateSort,
    updateFilters,
    setSearchQuery,
    resetConfig,
    currentConfig: config,
  };
};
